import type { PourCard } from '@/lib/apiClient';
import { POUR_TYPE_LABEL, pourStatusLabel } from './pourShared';

/**
 * The Pour Log: every pour card as a flat, filterable, sortable table with CY
 * totals and a CSV export — built for reconciling how many yards went into a
 * job (PM ask, 2026-09-24). Pure functions here so the arithmetic is tested;
 * PourLogTable is only the UI.
 *
 * Two quantities, never blended:
 *   • Ordered CY — the leading number of what the field wrote ("80 plus" → 80).
 *   • Actual CY  — from the job's Final Quantity Report, when one matched.
 * Blending them ("actual else ordered") would hide which figure a total is
 * made of, which is the whole question when reconciling.
 */

// Concrete only. Rock/Aggregate deliveries have their own log (rockLog.ts):
// they're ordered in loads or tons, so they have no place in CY totals.
export const POUR_TYPES = ['main', 'willcall', 'mudseal'] as const;
export const POUR_STATUSES = ['pending', 'confirmed', 'poured', 'covered', 'cancelled'] as const;

/**
 * What the log shows before anyone touches a filter: the pours that are real.
 *
 * A will-call is only a placeholder — nothing is confirmed or poured from it,
 * whether it later matched the main pour or not (PE, 2026-09-24). Counting its
 * yards beside the main pour's would inflate a reconciliation, so will-calls
 * start filtered out BY TYPE. Cancelled orders never poured either. All are
 * one click away.
 *
 * 'covered' is a will-call-only status, so it stays ON here: with will-calls
 * off it matches nothing, and turning the Will-Call type on then shows every
 * will-call rather than silently hiding the covered ones.
 */
export const DEFAULT_TYPES: readonly string[] = ['main', 'mudseal'];
export const DEFAULT_STATUSES: readonly string[] = ['pending', 'confirmed', 'poured', 'covered'];

/** Lifecycle order for the Status column — the work queue's order, not the
 *  alphabet (which put Pending between Order Confirmed and Poured). */
const STATUS_ORDER: Record<string, number> = { pending: 1, confirmed: 2, poured: 3, covered: 4, cancelled: 5 };

export interface PourLogFilter {
  search: string;
  types: readonly string[];
  statuses: readonly string[];
  /** Inclusive YYYY-MM-DD bounds on the pour's wall-clock date; '' = open. */
  from: string;
  to: string;
}

export type PourLogSortKey =
  | 'job' | 'pourDate' | 'type' | 'element' | 'mix' | 'orderedCy' | 'actualCy' | 'actualDate' | 'vendor' | 'status'
  | 'source';

export interface PourLogSort { key: PourLogSortKey; dir: 1 | -1 }

/**
 * The pour's date as typed on the form. pour_datetime is stored as wall-clock
 * digits in UTC (see fmtDt in pourShared), so the UTC calendar date IS the
 * date the field meant — reading it in the browser zone would move an early
 * pour to the previous day for anyone west of UTC.
 */
export function wallDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

export function wallTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(11, 16);
}

export function jobLabel(c: PourCard): string {
  return c.project?.jobNumber ?? c.jobNumberRaw ?? '';
}

export function filterPourLog(cards: readonly PourCard[], f: PourLogFilter): PourCard[] {
  const q = f.search.trim().toLowerCase();
  return cards.filter((c) => {
    if (!f.types.includes(c.pourType)) return false;
    if (!f.statuses.includes(c.status)) return false;
    if (f.from || f.to) {
      const d = wallDate(c.pourDatetime);
      // A card with no pour date can't be placed in a range, so a range excludes it.
      if (!d) return false;
      if (f.from && d < f.from) return false;
      if (f.to && d > f.to) return false;
    }
    if (q) {
      const hay = [jobLabel(c), c.project?.title, c.element, c.location, c.mix, c.vendor, c.costCode];
      if (!hay.some((v) => v != null && v.toLowerCase().includes(q))) return false;
    }
    return true;
  });
}

function sortValue(c: PourCard, key: PourLogSortKey): string | number | null {
  switch (key) {
    case 'job':       return jobLabel(c) || null;
    case 'pourDate':  return c.pourDatetime ? new Date(c.pourDatetime).getTime() : null;
    case 'type':      return POUR_TYPE_LABEL[c.pourType] ?? c.pourType;
    case 'element':   return c.element;
    case 'mix':       return c.mix;
    case 'orderedCy': return c.qtyCy;
    case 'actualCy':  return c.actualQtyCy;
    case 'actualDate': return c.actualPourAt ? new Date(c.actualPourAt).getTime() : null;
    case 'vendor':    return c.vendor;
    case 'status':    return STATUS_ORDER[c.status] ?? 99;
    case 'source':    return c.source === 'manual' ? 'Office entry' : 'Field form';
  }
}

/** Blanks sort LAST in both directions — flipping to descending shouldn't
 *  bury the real values under a pile of empty rows. Ties keep pour-date order. */
export function sortPourLog(cards: readonly PourCard[], s: PourLogSort): PourCard[] {
  return sortCardsBy(cards, (c) => sortValue(c, s.key), s.dir);
}

/**
 * The logs' shared sort (Pour Log and Rock Log). Each row's key is computed
 * once up front, not inside the comparator, so a key that parses text costs
 * n parses rather than n log n.
 */
export function sortCardsBy(
  cards: readonly PourCard[],
  valueOf: (c: PourCard) => string | number | null,
  dir: 1 | -1,
): PourCard[] {
  const keys = new Map(cards.map((c) => [c, valueOf(c)] as const));
  return [...cards].sort((a, b) => {
    const va = keys.get(a) ?? null;
    const vb = keys.get(b) ?? null;
    const aBlank = va == null || va === '';
    const bBlank = vb == null || vb === '';
    if (aBlank !== bBlank) return aBlank ? 1 : -1;
    let cmp = 0;
    if (!aBlank) {
      cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    }
    if (cmp !== 0) return dir * cmp;
    const ta = a.pourDatetime ? new Date(a.pourDatetime).getTime() : Infinity;
    const tb = b.pourDatetime ? new Date(b.pourDatetime).getTime() : Infinity;
    return ta === tb ? a.id - b.id : ta - tb;
  });
}

export interface PourLogTotals {
  rows: number;
  orderedCy: number;
  actualCy: number;
  /** Rows with a written quantity that isn't a number ("TBD") — left out of
   *  the Ordered total, and counted so the total can't be silently short. */
  unreadableQty: number;
  /** Rows with no Final Quantity Report — left out of the Actual total. */
  noActual: number;
}

export function pourLogTotals(cards: readonly PourCard[]): PourLogTotals {
  const t: PourLogTotals = { rows: cards.length, orderedCy: 0, actualCy: 0, unreadableQty: 0, noActual: 0 };
  for (const c of cards) {
    if (c.qtyCy != null) t.orderedCy += c.qtyCy;
    else if (c.qtyText && c.qtyText.trim() !== '') t.unreadableQty += 1;
    if (c.actualQtyCy != null) t.actualCy += c.actualQtyCy;
    else t.noActual += 1;
  }
  // Two-decimal columns summed in floating point drift (0.1 + 0.2); the
  // source column is DECIMAL(10,2), so round the sum back to that.
  t.orderedCy = Math.round(t.orderedCy * 100) / 100;
  t.actualCy = Math.round(t.actualCy * 100) / 100;
  return t;
}

/**
 * Final Quantity Report yards sitting on rows the TYPE or STATUS filter is
 * hiding — the search and date range still apply, so this answers "what
 * reported yards for these pours am I not seeing?".
 *
 * The matcher now prefers real pours, but it used to take the newest card of
 * any kind, so a will-call can already carry a job's reported actuals. Hiding
 * will-calls by default would then drop those yards from the Actual total
 * without a word (Codex on #555); this is what the table warns with.
 */
export function hiddenActuals(cards: readonly PourCard[], f: PourLogFilter): { rows: number; cy: number } {
  const shown = new Set(filterPourLog(cards, f).map((c) => c.id));
  const hidden = filterPourLog(cards, { ...f, types: POUR_TYPES, statuses: POUR_STATUSES })
    .filter((c) => !shown.has(c.id) && c.actualQtyCy != null);
  const cy = hidden.reduce((s, c) => s + (c.actualQtyCy ?? 0), 0);
  return { rows: hidden.length, cy: Math.round(cy * 100) / 100 };
}

/**
 * One CSV cell. Quoted when it needs to be, and — because element, location
 * and vendor are free text typed on a phone — a leading = + - @ is defused
 * with an apostrophe so Excel shows it instead of evaluating it as a formula.
 * Numbers are written bare so Excel can sum them.
 */
export function csvCell(v: string | number | null | undefined): string {
  if (v == null) return '';
  if (typeof v === 'number') return String(v);
  let s = v;
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function pourLogCsv(cards: readonly PourCard[]): string {
  const header = [
    'Job #', 'Job name', 'Pour date', 'Pour time', 'Pour type', 'Element', 'Location', 'Mix ID',
    'Qty as written', 'Ordered CY', 'Actual CY', 'Actual pour date', 'Vendor', 'Cost code',
    'Field leader', 'Status', 'Entered by',
  ];
  const rows = cards.map((c) => [
    jobLabel(c),
    c.project?.title ?? null,
    wallDate(c.pourDatetime),
    wallTime(c.pourDatetime),
    POUR_TYPE_LABEL[c.pourType] ?? c.pourType,
    c.element,
    c.location,
    c.mix,
    c.qtyText,
    c.qtyCy,
    c.actualQtyCy,
    wallDate(c.actualPourAt),
    c.vendor,
    c.costCode,
    c.superName,
    pourStatusLabel(c.status, c.pourType),
    c.source === 'manual' ? `Office${c.createdBy ? ` (${c.createdBy})` : ''}` : 'Field form',
  ]);
  // CRLF + a UTF-8 BOM: what Excel on Windows needs to open it with the right
  // columns and without mangling "—" or accented names.
  return '﻿' + [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
